/* notifications.svelte.ts — persistent log of meaningful UI events.
 *
 * Why this exists: equip warnings + admin/rate-limit nudges used to live
 * only in transient toasts (2.4s lifetime). Users who looked away missed
 * them. This store keeps the last N entries in IndexedDB so the header
 * bell can show an unread badge across reloads.
 *
 * Storage: idb-keyval key `notif-log:v1` → NotifEntry[] (newest first).
 * Capped at 50 entries; older trimmed on push. Read state is per-device
 * via `lastReadAt` epoch ms, also persisted. No PII — only counts +
 * short subject strings.
 */
import { get as idbGet, set as idbSet } from 'idb-keyval';
import { toast } from '$domain/toasts.svelte';

export type NotifLevel = 'info' | 'warn' | 'error';

export interface NotifEntry {
  id: string;
  level: NotifLevel;
  title: string;
  body: string;
  at: number;
  /** Optional grouping tag (e.g., 'equip', 'rate-limit', 'manifest'). */
  source?: string;
  /** When set, the entry represents a collapsed batch (e.g., 8 equip
   *  warnings from the same loadout). UI renders a chevron and expands
   *  inline. `body` summarises the count; `groupItems` holds details. */
  kind?: 'single' | 'group';
  groupItems?: { title: string; body: string }[];
}

interface NotifState {
  entries: NotifEntry[];
  lastReadAt: number;
}

const KEY = 'notif-log:v1';
const MAX_ENTRIES = 50;

const store = $state<NotifState>({ entries: [], lastReadAt: 0 });
let loaded = false;
let saveDebounce: ReturnType<typeof setTimeout> | null = null;

async function load(): Promise<void> {
  if (loaded) return;
  loaded = true;
  try {
    const raw = (await idbGet(KEY)) as NotifState | undefined;
    if (raw && Array.isArray(raw.entries)) {
      store.entries = raw.entries.slice(0, MAX_ENTRIES);
      store.lastReadAt = typeof raw.lastReadAt === 'number' ? raw.lastReadAt : 0;
    }
  } catch {
    /* IDB unavailable in some sandboxed contexts; in-memory is fine. */
  }
}

function flush(): void {
  if (saveDebounce) clearTimeout(saveDebounce);
  saveDebounce = setTimeout(() => {
    void idbSet(KEY, $state.snapshot(store)).catch(() => {});
  }, 200);
}

/** Public read-only accessor. Triggers async load on first call. */
export function notifications(): NotifState {
  if (!loaded) void load();
  return store;
}

export function unreadCount(): number {
  return store.entries.filter((e) => e.at > store.lastReadAt).length;
}

/** Emit a transient toast that links back to the bell. Only fires for
 *  warn/error since `info` events are already noisy in normal flows.
 *  Uses the global opener exposed by NotificationCenter; falls back to
 *  no-op if the bell isn't mounted (admin pages etc.). */
function bridgeToast(level: NotifLevel, title: string, body: string): void {
  if (level === 'info') return;
  const w = window as unknown as { __cryptarchOpenNotifications?: () => void };
  const opener = w.__cryptarchOpenNotifications;
  const action = opener ? { label: 'see in 🔔', onClick: opener } : undefined;
  const message = body ? `${title} — ${body}` : title;
  if (level === 'warn') toast.warn(message, { action });
  else toast.error(message, { action });
}

/** Append a single entry. Newest first; older entries trimmed past
 *  MAX_ENTRIES. Generates a stable id from time + random to dedupe
 *  rapid pushes inside the same tick. */
export function pushNotification(
  level: NotifLevel,
  title: string,
  body: string,
  source?: string,
): void {
  if (!loaded) void load();
  const entry: NotifEntry = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    level,
    title,
    body,
    at: Date.now(),
    source,
    kind: 'single',
  };
  store.entries = [entry, ...store.entries].slice(0, MAX_ENTRIES);
  flush();
  bridgeToast(level, title, body);
}

/** Bulk push — used by the equip pipeline to fan out an EquipWarning[]
 *  in one go without thrashing the IDB writer. When items.length >= 5
 *  the batch is collapsed into a single `group` entry so the bell list
 *  doesn't get drowned. */
const GROUP_THRESHOLD = 5;
export function pushNotifications(
  level: NotifLevel,
  source: string,
  items: { title: string; body: string }[],
): void {
  if (items.length === 0) return;
  if (!loaded) void load();
  const now = Date.now();
  if (items.length >= GROUP_THRESHOLD) {
    const group: NotifEntry = {
      id: `${now}-grp-${Math.random().toString(36).slice(2, 6)}`,
      level,
      title: `${items.length} ${source} ${level === 'error' ? 'errors' : 'warnings'}`,
      body: `${items[0]!.title}${items.length > 1 ? ` (+${items.length - 1} more)` : ''}`,
      at: now,
      source,
      kind: 'group',
      groupItems: items.slice(0, 20),
    };
    store.entries = [group, ...store.entries].slice(0, MAX_ENTRIES);
    flush();
    bridgeToast(level, group.title, items[0]!.title);
    return;
  }
  const fresh: NotifEntry[] = items.map((it, i) => ({
    id: `${now}-${i}-${Math.random().toString(36).slice(2, 6)}`,
    level,
    title: it.title,
    body: it.body,
    at: now,
    source,
    kind: 'single',
  }));
  store.entries = [...fresh, ...store.entries].slice(0, MAX_ENTRIES);
  flush();
  bridgeToast(level, fresh[0]!.title, fresh[0]!.body);
}

export function markAllRead(): void {
  store.lastReadAt = Date.now();
  flush();
}

export function clearAll(): void {
  store.entries = [];
  store.lastReadAt = Date.now();
  flush();
}
